import React, { MouseEvent, useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import RetryPaymentAdyenModal from '../RetryPaymentAdyenModal/RetryPaymentAdyenModal';
import RetryPaymentBraintreeModal from '../RetryPaymentBraintreeModal/RetryPaymentBraintreeModal';

import { useTracking } from '@hooks/useTracking';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { FraudPreventionRecommendation, PspConnector, RetryPaymentMethod } from '@types-internal';
import { hasText } from '@utils/utils';

import styles from './RetryNotification.module.scss';

export type RetryNotificationProps = {
  type?: 'success' | 'warning' | 'info' | 'error';
  id: string;
  ariaLabel: string;
  linkText?: string;
  linkUrl?: string;
  linkDataZta?: string;
  orderId?: string;
  retryPaymentMethod?: RetryPaymentMethod;
  connector?: PspConnector;
  pspToken?: string;
  amount?: number;
  currency?: string;
  siteId?: number;
  customerId?: string;
  sid?: string;
  fraudPreventionRecommendation?: FraudPreventionRecommendation;
};

const Notification: React.FC<RetryNotificationProps> = ({
  type = 'info',
  id,
  ariaLabel,
  children,
  linkText,
  linkUrl,
  retryPaymentMethod,
  orderId,
  connector,
  pspToken,
  amount,
  currency,
  siteId,
  sid,
  customerId,
  fraudPreventionRecommendation,
}) => {
  const [showBraintreeFormModal, setShowBraintreeFormModal] = useState(false);
  const [showAdyenFormModal, setShowAdyenFormModal] = useState(false);
  const track = useTracking({
    currency,
    customerId,
    sid,
    siteId,
    guest: false,
  });
  const sendAdobeTrackingClickEvent = () => {
    AdobeTrackingService.accountEventTracking('retry_payment', {
      payment_method: retryPaymentMethod,
      order_id: orderId,
    });
  };

  const handleOnClick = (e: MouseEvent<HTMLElement>) => {
    sendAdobeTrackingClickEvent();
    if (linkUrl && hasText(linkUrl)) {
      window.location.href = linkUrl;
      return;
    }

    if (connector?.toUpperCase() === 'BRAINTREE') {
      setShowBraintreeFormModal(!showBraintreeFormModal);
      e.preventDefault();
    }
    if (connector?.toUpperCase() === 'ADYEN') {
      setShowAdyenFormModal(!showAdyenFormModal);
      e.preventDefault();
    }
  };

  const isAdyenRetryEligible =
    connector === 'ADYEN' &&
    retryPaymentMethod === 'CREDIT_CARD' &&
    orderId &&
    siteId &&
    amount &&
    currency &&
    customerId;

  const isBraintreeRetryEligible =
    connector === 'BRAINTREE' && pspToken && amount && currency && orderId && siteId;

  return (
    <>
      <Alert
        className={styles.notification}
        variant={type}
        role="alertdialog"
        data-zta={id}
        aria-labelledby={ariaLabel}
        aria-describedby={id}
        aria-label={ariaLabel}
        buttonText={linkText}
        onButtonClick={handleOnClick}>
        {children}
      </Alert>

      {isBraintreeRetryEligible && (
        <RetryPaymentBraintreeModal
          show={showBraintreeFormModal}
          token={pspToken}
          onClose={() => setShowBraintreeFormModal(false)}
          amount={amount}
          currency={currency}
          orderId={orderId}
          siteId={siteId}
          customerId={customerId}
          fraudPreventionRecommendation={fraudPreventionRecommendation}
          track={track}
        />
      )}
      {isAdyenRetryEligible && (
        <RetryPaymentAdyenModal
          show={showAdyenFormModal}
          onClose={() => setShowAdyenFormModal(false)}
          orderId={orderId}
          siteId={siteId}
          customerId={customerId}
          amount={amount}
          currency={currency}
        />
      )}
    </>
  );
};

export default Notification;
